import React from "react";
import { Link } from "react-router-dom";
import {
  Activity, FlaskConical, Sparkles, Dumbbell, Briefcase,
  Heart, Target, ArrowRight, CheckCircle2, Shield
} from "lucide-react";
import Testimonials from "@/components/aevos/Testimonials";

const Pillar = ({ letter, title, desc }) => (
  <div className="aevos-card p-6">
    <div className="flex items-center gap-3 mb-3">
      <span className="font-display font-bold text-3xl text-[#00C4FF]">{letter}</span>
      <div className="w-10 h-px bg-black" />
    </div>
    <h3 className="font-display font-semibold text-lg text-black">{title}</h3>
    <p className="text-sm text-[#333333] mt-2 leading-relaxed">{desc}</p>
  </div>
);

const TechCard = ({ Icon, title, desc }) => (
  <div className="aevos-card p-6 group">
    <div className="w-11 h-11 border border-black flex items-center justify-center mb-4 group-hover:bg-[#00C4FF] group-hover:border-[#00C4FF] transition-colors">
      <Icon size={20} className="text-black" />
    </div>
    <h3 className="font-display font-semibold text-base text-black">{title}</h3>
    <p className="text-sm text-[#333333] mt-2">{desc}</p>
  </div>
);

const ProfileTile = ({ Icon, label, slug }) => (
  <Link
    to={`/mapeamento?perfil=${slug}`}
    className="aevos-card p-5 flex items-center gap-4 group"
    data-testid={`profile-tile-${slug}`}
  >
    <div className="w-12 h-12 border border-[#E0E0E0] flex items-center justify-center group-hover:border-[#00C4FF] transition-colors">
      <Icon size={18} className="text-black" />
    </div>
    <div className="flex-1">
      <p className="font-display font-medium text-sm text-black">{label}</p>
      <p className="text-xs text-[#666] mt-0.5">Fazer mapeamento →</p>
    </div>
    <ArrowRight size={16} className="text-[#00C4FF] opacity-0 group-hover:opacity-100 transition-opacity" />
  </Link>
);

const PlanCard = ({ name, priceFull, priceEss, featured = false, items = [] }) => (
  <div className={`p-7 ${featured ? "plan-featured bg-[#E6F8FF]" : "aevos-card"}`}>
    <p className="aevos-eyebrow">AEVOS</p>
    <h3 className="font-display font-bold text-2xl text-black mt-2">{name}</h3>
    <div className="mt-5 pb-5 border-b border-[#E0E0E0]">
      <p className="text-xs text-[#666] uppercase tracking-wider">Investimento</p>
      <p className="font-display font-bold text-3xl text-black mt-1">{priceFull}</p>
      {priceEss && <p className="text-xs text-[#666] mt-1">{priceEss}</p>}
    </div>
    <ul className="mt-5 space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-sm text-[#333333]">
          <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ONLINE_PLAN_ITEMS = [
  "4 consultas médicas online",
  "Análise de exames e estratégia personalizada",
  "Acompanhamento remoto por 4 meses",
];

const PERFORMANCE_PLAN_ITEMS = [
  "4 consultas médicas + 4 bioimpedâncias",
  "1 calorimetria + nutri + treino personalizado",
  "Monitoramento de evolução por 4 meses",
];

const ELITE_PLAN_ITEMS = [
  "Tudo do AEVOS Performance",
  "Monitoramento ampliado e prioridade de agenda",
  "2 aplicações terapêuticas conforme indicação médica",
];

const HeroSection = () => (
  <section className="aevos-molecule" data-testid="home-hero">
    <div className="aevos-container pt-8 md:pt-12 pb-8 md:pb-10 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="aevos-eyebrow mb-5 fade-up">Medicina preventiva · Performance · Longevidade</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[4rem] leading-[1.02] text-black fade-up delay-1">
          Ciência que <span className="text-[#00C4FF]">multiplica</span> vida.
        </h1>
        <p className="mt-6 text-[#333333] text-base md:text-lg max-w-xl fade-up delay-2">
          Protocolos personalizados de medicina preventiva, saúde metabólica e longevidade —
          construídos com dados clínicos reais e acompanhamento próximo de uma equipe especializada.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 fade-up delay-3">
          <Link to="/contato" data-testid="hero-cta-primary">
            <span className="btn-primary">Agendar Consulta <ArrowRight size={16} /></span>
          </Link>
          <Link to="/mapeamento" data-testid="hero-cta-secondary">
            <span className="btn-ghost">Descobrir meu perfil metabólico</span>
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6 fade-up delay-4">
          <div className="flex items-center gap-2 text-xs text-[#666]">
            <Shield size={14} className="text-[#00C4FF]" />
            <span>CRM SP 200364 · RQE 120948</span>
          </div>
          <div className="text-xs text-[#666]">Atende Presencial e Online</div>
        </div>
      </div>

      <div className="relative" data-testid="hero-visual">
        <div className="w-full flex items-center justify-center">
          <img
            src="/assets/hero-design.svg"
            alt="Performance, saúde metabólica e longevidade — Clínica AEVOS"
            className="w-full h-auto object-contain hero-float"
            loading="eager"
          />
        </div>
      </div>
    </div>
  </section>
);

const MethodSection = () => (
  <section className="pt-6 md:pt-10 pb-20 md:pb-28" data-testid="home-method">
    <div className="aevos-container">
      <div className="max-w-3xl">
        <div className="aevos-eyebrow mb-3">O Método AEVOS</div>
        <h2 className="font-display font-bold text-3xl md:text-5xl text-black leading-[1.1]">
          Não é uma consulta. É um <span className="text-[#00C4FF]">protocolo</span> de transformação.
        </h2>
        <p className="mt-5 text-[#333333] text-base md:text-lg">
          O Método MAP combina medicina, nutrição e tecnologia em 4 meses de acompanhamento
          integrado — ajustado mês a mês à sua biologia, à sua rotina e aos seus objetivos.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        <Pillar
          letter="M"
          title="Mapear"
          desc="Avaliação clínica profunda, exames laboratoriais, bioimpedância de precisão e calorimetria indireta para entender sua biologia real."
        />
        <Pillar
          letter="A"
          title="Ajustar"
          desc="Construção de um protocolo individual: estratégia nutricional, treino, sono, suplementação e — quando indicado — terapias adjuvantes."
        />
        <Pillar
          letter="P"
          title="Progredir"
          desc="Reavaliações periódicas, leitura objetiva dos dados e ajustes finos — para evoluções consistentes e sustentáveis."
        />
      </div>

      <div className="mt-10">
        <Link to="/metodo" data-testid="home-method-cta">
          <span className="btn-ghost">Conhecer o Método AEVOS <ArrowRight size={16} /></span>
        </Link>
      </div>
    </div>
  </section>
);

const TechSection = () => (
  <section className="section-pad bg-[#E6F8FF] border-y border-[#E0E0E0]" data-testid="home-tech">
    <div className="aevos-container">
      <div className="max-w-3xl">
        <div className="aevos-eyebrow mb-3">Tecnologia & Exames</div>
        <h2 className="font-display font-bold text-3xl md:text-5xl text-black leading-[1.1]">
          Decisões clínicas baseadas em <span className="text-[#00C4FF]">dados reais</span>, não em estimativas.
        </h2>
      </div>

      <div className="mt-12 grid sm:grid-cols-3 gap-5">
        <TechCard
          Icon={Activity}
          title="Bioimpedância BodyMetrix"
          desc="Ultrassom portátil que mostra, em tempo real, a composição corporal sem estimativas."
        />
        <TechCard
          Icon={FlaskConical}
          title="Calorimetria Indireta"
          desc="Mede com precisão o gasto calórico real e o uso de gorduras vs. carboidratos."
        />
        <TechCard
          Icon={Sparkles}
          title="Protocolo AEVOS"
          desc="Protocolos terapêuticos personalizados para saúde, performance e longevidade."
        />
      </div>

      <div className="mt-10">
        <Link to="/tecnologia" data-testid="home-tech-cta">
          <span className="btn-primary">Conhecer nossa tecnologia <ArrowRight size={16} /></span>
        </Link>
      </div>
    </div>
  </section>
);

const ProfilesSection = () => (
  <section className="pt-20 md:pt-28 pb-8 md:pb-10" data-testid="home-profiles">
    <div className="aevos-container">
      <div className="max-w-3xl">
        <div className="aevos-eyebrow mb-3">Perfis de paciente</div>
        <h2 className="font-display font-bold text-3xl md:text-5xl text-black leading-[1.1]">
          Você se identifica com algum desses perfis?
        </h2>
        <p className="mt-5 text-[#333333] text-base">
          Selecione um perfil abaixo para iniciar seu Mapeamento Metabólico <span className="bg-black text-white px-2 py-0.5">gratuito</span> e receba uma leitura personalizada do seu perfil agora mesmo.
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ProfileTile Icon={Dumbbell} label="Praticante de academia" slug="academia" />
        <ProfileTile Icon={Target} label="Atleta amador" slug="atleta" />
        <ProfileTile Icon={Sparkles} label="Dificuldade de emagrecer" slug="emagrecer" />
        <ProfileTile Icon={Briefcase} label="Executivo de alta performance" slug="executivo" />
        <ProfileTile Icon={Heart} label="Saúde desorganizada" slug="saude" />
      </div>
    </div>
  </section>
);

const PlansSection = () => (
  <section className="pt-8 md:pt-12 pb-20 md:pb-28" data-testid="home-plans">
    <div className="aevos-container">
      <div className="max-w-3xl">
        <div className="aevos-eyebrow mb-3">Planos & Investimento</div>
        <h2 className="font-display font-bold text-3xl md:text-5xl text-black leading-[1.1]">
          Três programas, uma <span className="text-[#00C4FF]">filosofia</span>.
        </h2>
        <p className="mt-4 text-[#333333]">
          Acompanhamento integrado de 4 meses — escolha a profundidade que faz sentido para você.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        <PlanCard name="AEVOS Online" priceFull="R$ 1.980" priceEss="ou 4× de R$ 495" items={ONLINE_PLAN_ITEMS} />
        <PlanCard name="AEVOS Performance" priceFull="R$ 4.500" priceEss="ou R$ 4.275 via PIX" items={PERFORMANCE_PLAN_ITEMS} />
        <PlanCard name="AEVOS Elite" priceFull="R$ 5.500" priceEss="ou R$ 5.225 via PIX" featured items={ELITE_PLAN_ITEMS} />
      </div>

      <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-sm text-[#666] max-w-2xl">
          Conheça também o <strong className="text-black">AEVOS 365</strong>, programa anual com
          acompanhamento contínuo. Todos os planos são personalizados após avaliação médica.
        </p>
        <Link to="/planos">
          <span className="btn-primary">Ver todos os planos <ArrowRight size={16} /></span>
        </Link>
      </div>
    </div>
  </section>
);

const AboutTeaserSection = () => (
  <section className="section-pad bg-black" data-testid="home-about">
    <div className="aevos-container grid lg:grid-cols-5 gap-10 items-center">
      <div className="lg:col-span-2">
        <div className="aspect-[4/5] border border-white/10 bg-white/5 overflow-hidden">
          <img
            src="/assets/dr-guilherme.jpg"
            alt="Dr. Guilherme Affonso"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="aevos-eyebrow mb-3">Sobre o Dr. Guilherme</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl !text-white leading-[1.1]">
          Medicina baseada em evidência. <br/>Acompanhamento humano de verdade.
        </h2>
        <p className="mt-5 text-white/80">
          Dr. Guilherme Affonso — médico com especialidade em Medicina Preventiva e Social
          (CRM SP 200364 · RQE 120948) — conduz na AEVOS uma equipe multidisciplinar focada em
          saúde metabólica, performance e longevidade.
        </p>
        <div className="mt-7">
          <Link to="/sobre" data-testid="home-about-cta">
            <span className="btn-ghost-on-dark">Conhecer o Dr. Guilherme <ArrowRight size={16} /></span>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const B2BSection = () => (
  <section className="py-10 md:py-14" data-testid="home-b2b">
    <div className="aevos-container border border-black p-10 md:p-16 grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <div className="aevos-eyebrow mb-3">Parcerias AEVOS</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-black leading-[1.1]">
          Sua marca ou empresa oferece saúde de verdade?
        </h2>
        <p className="mt-5 text-[#333333]">
          Conecte sua audiência a um padrão clínico AEVOS. Programas para estúdios, academias,
          marcas, equipes de RH e influenciadores.
        </p>
      </div>
      <div className="lg:text-right">
        <Link to="/parcerias" data-testid="home-b2b-cta">
          <span className="btn-primary">Quero ser parceiro <ArrowRight size={16} /></span>
        </Link>
      </div>
    </div>
  </section>
);

const FinalCtaSection = () => (
  <section className="bg-black text-white pt-10 md:pt-12 pb-10 md:pb-12" data-testid="home-final-cta">
    <div className="aevos-container text-center">
      <div className="font-display text-xs font-medium tracking-[0.18em] uppercase text-white mb-4 inline-block">Próximo passo</div>
      <h2 className="font-display font-bold text-3xl md:text-5xl leading-[1.1] max-w-3xl mx-auto !text-white">
        Pronto para construir uma rotina que sustenta sua <span className="text-[#00C4FF]">performance</span>?
      </h2>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/contato">
          <span className="btn-cyan">Agendar Consulta <ArrowRight size={16} /></span>
        </Link>
        <Link to="/mapeamento">
          <span className="btn-ghost-on-dark">Fazer Mapeamento Online</span>
        </Link>
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="page-home">
      <HeroSection />
      <MethodSection />
      <TechSection />
      <ProfilesSection />
      <PlansSection />
      <Testimonials />
      <AboutTeaserSection />
      <B2BSection />
      <FinalCtaSection />
    </div>
  );
};

export default Home;
